import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SelectDateTime — step 1 of guest booking flow.
 * Handles both date_only and time_slotted services.
 * Loads the service, checks capacity per day/slot, then adds to basket.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import CustomerLayout from '@/components/CustomerLayout';
import { addMonths, eachDayOfInterval, endOfMonth, format, getDay, isBefore, isSameDay, parseISO, startOfMonth, startOfToday, } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { formatGBP } from '@/lib/utils';
import { useGuestBooking } from '@/store/guestBooking';
// day abbreviation → date-fns day index (0=Sun)
const DAY_KEYS = { 0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat' };
export default function SelectDateTime() {
    const { serviceId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const addItem = useGuestBooking((s) => s.addItem);
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    const [calMonth, setCalMonth] = useState(startOfToday());
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    // capacity per date (date_only) or per date+time slot
    const [dayCapacity, setDayCapacity] = useState({}); // available spaces per date
    const [slotCapacity, setSlotCapacity] = useState({}); // key = "date|time"
    useEffect(() => {
        if (!serviceId)
            return;
        supabase
            .from('open_services')
            .select('*')
            .eq('id', serviceId)
            .eq('is_active', true)
            .single()
            .then(({ data }) => {
            if (!data) {
                navigate('/book', { replace: true });
                return;
            }
            setService(data);
            setLoading(false);
        });
    }, [serviceId]);
    // Load capacity for visible month
    useEffect(() => {
        if (!service)
            return;
        const start = startOfMonth(calMonth);
        const end = endOfMonth(calMonth);
        const days = eachDayOfInterval({ start, end });
        if (service.scheduling_type === 'date_only') {
            Promise.all(days.map((day) => supabase
                .rpc('get_open_service_capacity', {
                p_service_id: service.id,
                p_date: format(day, 'yyyy-MM-dd'),
                p_time: null,
            })
                .single()
                .then(({ data }) => ({ date: format(day, 'yyyy-MM-dd'), available: data?.available ?? 0 })))).then((results) => {
                setDayCapacity(Object.fromEntries(results.map((r) => [r.date, r.available])));
            });
        }
        else {
            // time_slotted: fetch capacity for every date+slot combo
            const fetches = [];
            days.forEach((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const dayKey = DAY_KEYS[day.getDay()];
                const slots = service.available_slots?.[dayKey] ?? [];
                slots.forEach((time) => {
                    fetches.push(Promise.resolve(supabase
                        .rpc('get_open_service_capacity', {
                        p_service_id: service.id,
                        p_date: dateStr,
                        p_time: time,
                    })
                        .single()
                        .then(({ data }) => ({ key: `${dateStr}|${time}`, data: data }))));
                });
            });
            Promise.all(fetches).then((results) => {
                setSlotCapacity(Object.fromEntries(results.map((r) => [r.key, r.data])));
                // Also compute per-day availability (max of any slot)
                const perDay = {};
                results.forEach(({ key, data }) => {
                    const date = key.split('|')[0];
                    perDay[date] = Math.max(perDay[date] ?? 0, data?.available ?? 0);
                });
                setDayCapacity(perDay);
            });
        }
    }, [calMonth, service]);
    function availableTimeSlotsForDate(dateStr) {
        if (!service?.available_slots)
            return [];
        const day = parseISO(dateStr);
        const dayKey = DAY_KEYS[day.getDay()];
        const times = service.available_slots[dayKey] ?? [];
        return times.map((time) => ({
            time,
            available: slotCapacity[`${dateStr}|${time}`]?.available ?? 0,
        }));
    }
    function handleBook() {
        if (!service || !selectedDate)
            return;
        addItem({
            service_id: service.id,
            service_name: service.name,
            scheduling_type: service.scheduling_type,
            booking_date: selectedDate,
            booking_time: selectedTime,
            amount_pence: service.price_pence,
        });
        navigate('/book/details');
    }
    if (loading || !service) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center", children: _jsx("p", { className: "text-gray-500", children: "Loading\u2026" }) }));
    }
    const today = startOfToday();
    const maxDate = addMonths(today, Math.ceil(service.booking_horizon_days / 30));
    const isTimeSlotted = service.scheduling_type === 'time_slotted';
    const timeSlots = selectedDate ? availableTimeSlotsForDate(selectedDate) : [];
    const canProceed = selectedDate && (!isTimeSlotted || selectedTime !== null);
    const pageContent = (_jsxs("main", { className: "max-w-xl mx-auto px-4 py-8", children: [_jsxs("div", { className: "flex items-center gap-3 mb-6", children: [_jsx(Link, { to: "/book", className: "text-sm text-gray-500 hover:text-gray-700", children: "\u2190 Services" }), _jsx("span", { className: "text-sm text-gray-400", children: "/" }), _jsxs("span", { className: "text-sm font-medium text-gray-700", children: ["Choose a Date", isTimeSlotted ? ' & Time' : ''] })] }), _jsxs("div", { className: "bg-white border border-gray-200 rounded-xl p-4 mb-6 flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("p", { className: "font-semibold text-gray-900", children: service.name }), service.duration_minutes && (_jsxs("p", { className: "text-xs text-gray-500 mt-0.5", children: [service.duration_minutes, " minutes"] }))] }), _jsx("span", { className: "font-semibold text-gray-900", children: formatGBP(service.price_pence) })] }), _jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsx("button", { onClick: () => setCalMonth((m) => addMonths(m, -1)), disabled: isSameDay(startOfMonth(calMonth), startOfMonth(today)), className: "p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30", children: "\u2190" }), _jsx("span", { className: "font-medium text-gray-800", children: format(calMonth, 'MMMM yyyy') }), _jsx("button", { onClick: () => setCalMonth((m) => addMonths(m, 1)), disabled: !isBefore(calMonth, addMonths(maxDate, -1)), className: "p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30", children: "\u2192" })] }), _jsxs("div", { className: "bg-white rounded-xl border border-gray-200 p-4 mb-6", children: [_jsx("div", { className: "grid grid-cols-7 mb-2", children: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (_jsx("div", { className: "text-xs font-medium text-gray-400 text-center py-1", children: d }, d))) }), _jsx(CalendarGrid, { month: calMonth, today: today, maxDate: maxDate, selectedDate: selectedDate, dayCapacity: dayCapacity, onSelect: (dateStr) => { setSelectedDate(dateStr); setSelectedTime(null); } })] }), isTimeSlotted && selectedDate && (_jsxs("div", { className: "mb-6", children: [_jsx("p", { className: "text-sm font-medium text-gray-700 mb-3", children: "Choose a time" }), timeSlots.length === 0 ? (_jsx("p", { className: "text-sm text-gray-500", children: "No slots available on this day." })) : (_jsx("div", { className: "grid grid-cols-3 gap-2", children: timeSlots.map(({ time, available }) => (_jsxs("button", { disabled: available === 0, onClick: () => setSelectedTime(time), className: `p-3 rounded-xl border-2 text-center transition-colors ${available === 0 ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed' :
                                selectedTime === time ? 'border-brand-green bg-brand-pale/30' :
                                    'border-gray-200 hover:border-blue-300'}`, children: [_jsx("p", { className: "font-medium text-sm text-gray-900", children: time }), _jsx("p", { className: "text-xs text-gray-400 mt-0.5", children: available === 0 ? 'Full' : `${available} left` })] }, time))) }))] })), !isTimeSlotted && selectedDate && dayCapacity[selectedDate] !== undefined && (_jsx("p", { className: "text-sm text-gray-500 mb-6", children: dayCapacity[selectedDate] === 0
                    ? 'Fully booked on this date.'
                    : `${dayCapacity[selectedDate]} space${dayCapacity[selectedDate] !== 1 ? 's' : ''} left` })), _jsx("button", { disabled: !canProceed, onClick: handleBook, className: "w-full py-3 bg-brand-green text-white font-medium rounded-lg hover:bg-brand-green/90 disabled:opacity-40 disabled:cursor-not-allowed", children: "Next \u2192" })] }));
    if (user) {
        return _jsx(CustomerLayout, { children: pageContent });
    }
    return (_jsxs("div", { className: "min-h-screen bg-brand-cream", children: [_jsxs("nav", { className: "bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between", children: [_jsx(Link, { to: "/book", className: "text-sm text-gray-500 hover:text-gray-700", children: "\u2190 Services" }), _jsxs("span", { className: "font-semibold text-gray-900", children: ["Choose a Date", isTimeSlotted ? ' & Time' : ''] }), _jsx("div", {})] }), pageContent] }));
}
// ── Calendar grid ─────────────────────────────────────────────────────────────
function CalendarGrid({ month, today, maxDate, selectedDate, dayCapacity, onSelect, }) {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    const days = eachDayOfInterval({ start, end });
    const offset = (getDay(start) + 6) % 7;
    return (_jsxs("div", { className: "grid grid-cols-7 gap-1", children: [Array.from({ length: offset }, (_, i) => _jsx("div", {}, `pad-${i}`)), days.map((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const isPast = isBefore(day, today);
                const isTooFar = !isBefore(day, maxDate);
                const available = dayCapacity[dateStr] ?? -1; // -1 = not loaded yet
                const isFull = available === 0;
                const disabled = isPast || isTooFar || isFull;
                const isSelected = selectedDate === dateStr;
                const isLow = available > 0 && available <= 3;
                return (_jsxs("button", { disabled: disabled, onClick: () => onSelect(dateStr), className: `aspect-square rounded-lg text-sm flex flex-col items-center justify-center gap-0.5 transition-colors ${disabled ? 'text-gray-300 cursor-not-allowed' :
                        isSelected ? 'bg-brand-green text-white font-bold' :
                            isLow ? 'bg-amber-50 text-amber-900 hover:bg-amber-100 font-medium' :
                                available > 0 ? 'bg-green-50 text-green-900 hover:bg-green-100 font-medium' :
                                    'text-gray-400'}`, children: [_jsx("span", { children: format(day, 'd') }), available > 0 && !isSelected && (_jsx("span", { className: "text-xs leading-none opacity-70", children: available }))] }, dateStr));
            })] }));
}
